import { FetchSource, PMTiles } from "pmtiles";
import { startTileServer } from "../webgl-maplibre/webgl-maplibre.tile-protocol";
import { listPackIds, readPackManifest } from "./dem-fs";

export interface EncodingContract {
  type: string;
  scale: number;
  offset: number;
  datum: string;
  tiling: string;
  format: string;
}

export interface PackManifest {
  id: string;
  version: number;
  title?: string;
  files: { base: string; max?: string };
  encoding: EncodingContract;
  surface_type?: string;
  bbox: [number, number, number, number];
  min_zoom: number;
  max_zoom: number;
  max_pyramid?: { min_zoom: number; max_zoom: number };
  native_resolution_m?: number;
  priority: number;
  attribution?: string;
}

export interface DemPack {
  manifest: PackManifest;
  archive: PMTiles;
  archiveUrl: string;
  archiveMax?: PMTiles;
  archiveMaxUrl?: string;
}

export const EXPECTED_ENCODING: Pick<EncodingContract, "type" | "scale" | "offset" | "tiling"> = {
  type: "terrain-rgb",
  scale: 1.0,
  offset: -10000,
  tiling: "web-mercator-xyz",
};

let packsPromise: Promise<DemPack[]> | null = null;
let packsResolved: DemPack[] | null = null;
const archiveCache = new Map<string, PMTiles>();

export function resetDemPacks(): void {
  packsPromise = null;
  packsResolved = null;
  archiveCache.clear();
}

export function getDemPacksSync(): DemPack[] | null {
  return packsResolved;
}

export function getArchive(archiveUrl: string): PMTiles {
  let archive = archiveCache.get(archiveUrl);
  if (!archive) {
    archive = new PMTiles(new FetchSource(archiveUrl));
    archiveCache.set(archiveUrl, archive);
  }
  return archive;
}

async function loadPack(packId: string, baseUrl: string): Promise<DemPack | null> {
  const manifestText = await readPackManifest(packId);
  if (manifestText == null) return null;

  let manifest: PackManifest;
  try {
    manifest = JSON.parse(manifestText) as PackManifest;
  } catch {
    console.warn(`dem-packs: invalid manifest for pack ${packId}`);
    return null;
  }

  const enc = manifest.encoding;
  if (
    !enc ||
    enc.type !== EXPECTED_ENCODING.type ||
    enc.scale !== EXPECTED_ENCODING.scale ||
    enc.offset !== EXPECTED_ENCODING.offset ||
    enc.tiling !== EXPECTED_ENCODING.tiling
  ) {
    console.warn(`dem-packs: unsupported encoding in pack ${packId}, skipping`);
    return null;
  }

  // Keep encoding symmetric with dem-fs.browser.ts (`encodeURIComponent(packId)`)
  // — otherwise a pack id containing reserved chars (`#`, `+`, ...) fetches
  // its manifest fine but every archive request 404s.
  const encId = encodeURIComponent(packId);
  const archiveUrl = `${baseUrl}/dem/${encId}/${manifest.files.base}`;
  const pack: DemPack = { manifest, archive: getArchive(archiveUrl), archiveUrl };
  if (manifest.files.max) {
    const archiveMaxUrl = `${baseUrl}/dem/${encId}/${manifest.files.max}`;
    pack.archiveMax = getArchive(archiveMaxUrl);
    pack.archiveMaxUrl = archiveMaxUrl;
  }
  return pack;
}

async function discoverPacks(): Promise<DemPack[]> {
  const baseUrl = await startTileServer();
  const ids = await listPackIds();
  const loaded = await Promise.all(ids.map((id) => loadPack(id, baseUrl)));
  const packs = loaded.filter((p): p is DemPack => p !== null);
  packs.sort((a, b) => b.manifest.priority - a.manifest.priority);
  return packs;
}

export function getDemPacks(): Promise<DemPack[]> {
  if (!packsPromise) {
    packsPromise = discoverPacks().then((p) => {
      packsResolved = p;
      // Don't memoize an empty result. First boot frequently races against
      // the app-server's first update-pull (web) or Directory.Data not yet
      // being populated (native); without this, a cold-start empty stays
      // empty until the user manually triggers resetDemPacks().
      if (p.length === 0) packsPromise = null;
      return p;
    });
  }
  return packsPromise;
}
